import { useEffect, useMemo, useRef, useState } from "react";

import { ROLE_LABELS } from "../../auth/permissions";
import "./PermissionEditor.css";

/**
 * Admin → Users → Edit permissions.
 *
 * ## The model it edits
 *
 * A role gives a default set; a user may carry per-permission exceptions on
 * top. Each row is therefore three-state — **Role default**, **Allow**,
 * **Deny** — and not a checkbox. A checkbox would collapse "the role grants
 * this" and "this user was specifically granted this" into one tick, and the
 * day the role changed nobody would be able to tell which users were
 * following it and which had been pinned.
 *
 * Every row says what the role would do and what the answer actually is, so
 * the effect of an override is visible before it is saved rather than
 * discovered later.
 *
 * ## What it is not
 *
 * Not authorization. The backend recalculates effective permissions from
 * MongoDB on every request and refuses anything the user does not hold,
 * whatever this screen last drew. Saving here writes the overrides; it does
 * not decide them.
 *
 * @param {{
 *   user: object,
 *   catalog: Array<{group: string, permissions: Array<{key: string, label: string, description: string}>}>,
 *   roleDefaults: Record<string, string[]>,
 *   isSaving?: boolean,
 *   error?: string|null,
 *   onSave: (overrides: Record<string, boolean>) => void,
 *   onClose: () => void,
 * }} props
 */

const CHOICE = Object.freeze({
  DEFAULT: "default",
  ALLOW: "allow",
  DENY: "deny",
});

const CHOICES = [
  { value: CHOICE.DEFAULT, label: "Role default" },
  { value: CHOICE.ALLOW, label: "Allow" },
  { value: CHOICE.DENY, label: "Deny" },
];

function choiceFor(overrides, key) {
  if (!Object.prototype.hasOwnProperty.call(overrides, key)) return CHOICE.DEFAULT;
  return overrides[key] ? CHOICE.ALLOW : CHOICE.DENY;
}

/** The editor's three-state rows back into the map the API stores. */
export function toOverrides(choices) {
  const overrides = {};
  for (const [key, choice] of Object.entries(choices)) {
    if (choice === CHOICE.ALLOW) overrides[key] = true;
    if (choice === CHOICE.DENY) overrides[key] = false;
  }
  return overrides;
}

/** What a row resolves to once the role and the override are combined. */
export function effectiveFor(choice, roleGrants) {
  if (choice === CHOICE.ALLOW) return true;
  if (choice === CHOICE.DENY) return false;
  return roleGrants;
}

function PermissionEditor({
  user,
  catalog,
  roleDefaults,
  isSaving = false,
  error = null,
  onSave,
  onClose,
}) {
  const initial = useMemo(() => {
    const overrides = user.permission_overrides || {};
    const choices = {};
    for (const group of catalog) {
      for (const permission of group.permissions) {
        choices[permission.key] = choiceFor(overrides, permission.key);
      }
    }
    return choices;
  }, [user, catalog]);

  const [choices, setChoices] = useState(initial);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => setChoices(initial), [initial]);

  // Focus moves into the dialog on open, so a keyboard user is not left
  // tabbing through the table behind it.
  useEffect(() => {
    if (closeRef.current) closeRef.current.focus();
  }, []);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      // A modal that lets focus escape behind itself is a modal in
      // appearance only.
      const focusable = dialogRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const rolePermissions = roleDefaults[user.role] || [];
  const overrideCount = Object.values(choices).filter(
    (choice) => choice !== CHOICE.DEFAULT
  ).length;

  const isDirty = useMemo(
    () => Object.keys(initial).some((key) => initial[key] !== choices[key]),
    [initial, choices]
  );

  function setChoice(key, value) {
    setChoices((current) => ({ ...current, [key]: value }));
  }

  function resetAll() {
    setChoices(
      Object.fromEntries(Object.keys(choices).map((key) => [key, CHOICE.DEFAULT]))
    );
  }

  return (
    <div className="perm-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="perm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="perm-title"
        ref={dialogRef}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="perm-head">
          <div>
            <h2 id="perm-title">Edit permissions</h2>
            <p className="perm-subject">
              {user.email}
              <span className="perm-role">{ROLE_LABELS[user.role] || user.role}</span>
            </p>
          </div>
          <button
            type="button"
            className="perm-close"
            onClick={onClose}
            ref={closeRef}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <p className="perm-explainer">
          Each permission follows the {ROLE_LABELS[user.role] || user.role} role
          unless it is set to Allow or Deny for this person specifically.
          {overrideCount > 0 ? (
            <>
              {" "}
              <strong>
                {overrideCount} exception{overrideCount === 1 ? "" : "s"}
              </strong>{" "}
              {overrideCount === 1 ? "currently applies" : "currently apply"}.
            </>
          ) : null}
        </p>

        {error ? (
          <div className="perm-error" role="alert">
            {error}
          </div>
        ) : null}

        <div className="perm-body">
          {catalog.map((group) => (
            <section className="perm-group" key={group.group}>
              <h3>{group.group}</h3>
              {group.permissions.map((permission) => {
                const roleGrants = rolePermissions.includes(permission.key);
                const choice = choices[permission.key] || CHOICE.DEFAULT;
                const effective = effectiveFor(choice, roleGrants);

                return (
                  <div className="perm-row" key={permission.key}>
                    <div className="perm-row__text">
                      <strong>{permission.label}</strong>
                      <span>{permission.description}</span>
                      <span className="perm-row__meta">
                        Role default: {roleGrants ? "Allowed" : "Denied"}
                        <span aria-hidden="true"> · </span>
                        Effective:{" "}
                        <span className={effective ? "is-allowed" : "is-denied"}>
                          {effective ? "Allowed" : "Denied"}
                        </span>
                      </span>
                    </div>

                    <div
                      className="perm-choice"
                      role="radiogroup"
                      aria-label={permission.label}
                    >
                      {CHOICES.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          role="radio"
                          aria-checked={choice === option.value}
                          className={`perm-choice__option${
                            choice === option.value ? " is-active" : ""
                          }`}
                          onClick={() => setChoice(permission.key, option.value)}
                          disabled={isSaving}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </section>
          ))}
        </div>

        <footer className="perm-foot">
          <button
            type="button"
            className="perm-btn"
            onClick={resetAll}
            disabled={isSaving || overrideCount === 0}
          >
            Clear all exceptions
          </button>
          <div className="perm-foot__right">
            <button type="button" className="perm-btn" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button
              type="button"
              className="perm-btn perm-btn--primary"
              onClick={() => onSave(toOverrides(choices))}
              disabled={isSaving || !isDirty}
            >
              {isSaving ? "Saving…" : "Save permissions"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default PermissionEditor;
